
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Photo } from '@/components/PhotoGrid';
import { X, Image, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

// Image host base URL (new host stores absolute URLs in config)
const API_BASE_URL = 'https://cdn-bun.stardots.io/swainphoto';
const resolvePhotoUrl = (path: string) => path.startsWith('http') ? path : `${API_BASE_URL}/${path}`;
const dummyPhotos: Photo[] = [
  {
    id: '1',
    title: '暮色山川',
    description: '夕阳西下，山峦起伏如海浪般层层叠叠，光影交织出大自然的壮丽画卷。',
    url: 'https://images.unsplash.com/photo-1469474968028-56623f02e42e?auto=format&fit=crop&w=1200',
    thumbnailUrl: 'https://images.unsplash.com/photo-1469474968028-56623f02e42e?auto=format&fit=crop&w=800',
    width: 1200,
    height: 800,
    category: '风景',
    createdAt: '2023-05-15T12:00:00Z'
  },
  {
    id: '2',
    title: '城市几何',
    description: '现代城市建筑的几何线条与结构之美，展现了人类智慧与创造力的结晶。',
    url: 'https://images.unsplash.com/photo-1486718448742-163732cd1544?auto=format&fit=crop&w=1200',
    thumbnailUrl: 'https://images.unsplash.com/photo-1486718448742-163732cd1544?auto=format&fit=crop&w=800',
    width: 800,
    height: 1200,
    category: '建筑',
    createdAt: '2023-06-22T15:30:00Z'
  },
  {
    id: '3',
    title: '海洋律动',
    description: '大海有着永不停歇的律动，波涛汹涌处是生命与力量的交响曲。',
    url: 'https://images.unsplash.com/photo-1500375592092-40eb2168fd21?auto=format&fit=crop&w=1200',
    thumbnailUrl: 'https://images.unsplash.com/photo-1500375592092-40eb2168fd21?auto=format&fit=crop&w=800',
    width: 1200,
    height: 900,
    category: '自然',
    createdAt: '2023-07-08T09:15:00Z'
  },
  {
    id: '4',
    title: '晨雾山林',
    description: '清晨的薄雾笼罩着山林，如梦似幻的景色让人仿佛置身仙境。',
    url: 'https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?auto=format&fit=crop&w=1200',
    thumbnailUrl: 'https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?auto=format&fit=crop&w=800',
    width: 1200,
    height: 800,
    category: '风景',
    createdAt: '2023-08-14T06:45:00Z'
  },
];

const Dashboard = () => {
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchPhotos = async () => {
      try {
        setLoading(true);
        const response = await fetch(`/photos.json`);
        const data = await response.json();
        
        // Transform data to match Photo interface
        const transformedPhotos = data.map((photo: any): Photo => ({
          id: photo.id,
          title: photo.title,
          description: photo.description,
          url: resolvePhotoUrl(photo.full_url),
          thumbnailUrl: resolvePhotoUrl(photo.preview_url),
          width: photo.width,
          height: photo.height,
          category: photo.category !== 'uncategorized' ? photo.category : undefined,
          createdAt: photo.date || new Date().toISOString()
        }));
        
        setPhotos(transformedPhotos);
      } catch (error) {
        console.error('Error fetching photos:', error);
        // Fallback to dummy data
        setPhotos(dummyPhotos);
        toast.error('获取照片数据失败，显示示例数据');
      } finally {
        setLoading(false);
      }
    };
    
    fetchPhotos();
  }, []);

  const handleDeletePhoto = (id: string) => {
    toast.success('照片已删除');
    setPhotos(photos.filter(photo => photo.id !== id));
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="max-w-7xl mx-auto">
      <header className="flex flex-col md:flex-row items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-playfair">管理后台</h1>
          <p className="text-muted-foreground mt-1">管理和上传您的摄影作品</p>
        </div>
        
        <div className="mt-4 md:mt-0 flex items-center">
          <Button 
            asChild
            variant="ghost" 
          >
            <a href="/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2">
              <Image className="h-4 w-4" />
              查看网站
            </a>
          </Button>
        </div>
      </header>
      
      <Tabs defaultValue="manage" className="mt-6">
        
        <TabsContent value="manage" className="space-y-6">
          <div className="bg-card p-6 rounded-lg shadow-sm">
            <h2 className="text-xl font-playfair mb-6">我的作品</h2>
            
            {loading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground mb-4" />
                <p className="text-muted-foreground">加载照片中...</p>
              </div>
            ) : photos.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground">暂无照片</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left font-medium py-3">缩略图</th>
                      <th className="text-left font-medium py-3">标题</th>
                      <th className="text-left font-medium py-3 hidden md:table-cell">分类</th>
                      <th className="text-left font-medium py-3 hidden md:table-cell">上传日期</th>
                    </tr>
                  </thead>
                  <tbody>
                    {photos.map(photo => (
                    <tr key={photo.id} className="border-b">
                      <td className="py-3">
                        <div className="w-16 h-16 overflow-hidden rounded-sm">
                          <img 
                            src={photo.thumbnailUrl} 
                            alt={photo.title} 
                            className="w-full h-full object-cover"
                          />
                        </div>
                      </td>
                      <td className="py-3">
                        <div className="font-medium">{photo.title}</div>
                        <div className="text-sm text-muted-foreground md:hidden">
                          {photo.category}
                        </div>
                      </td>
                      <td className="py-3 hidden md:table-cell">
                        {photo.category}
                      </td>
                      <td className="py-3 hidden md:table-cell">
                        {formatDate(photo.createdAt)}
                      </td>
                    </tr>
                  ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Dashboard;
